import type { BaseSyntheticEvent } from 'react';
import { useCallback, useMemo, useRef, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { type LOAN_REQUEST_USAGE, LOAN_REQUEST_USAGES } from 'cpool-constants';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import useLead from '../hooks/useLead';
import { setQueryPosition } from '~/store/queryPositionStore';
import { createRequiredContext } from '~/utils/createRequiredContext';
import { queryKeys } from '~/utils/queryKeys';

type DirtyState = { dirty: boolean; dirtyFields: Record<string, unknown> };

const isDirtyState = (dirtyState: DirtyState | null, usage: LOAN_REQUEST_USAGE | null | undefined) => {
    if (dirtyState === null) return false;

    const canIgnoreCarFinance =
        // if the usage type is not FAHRZEUGKAUF and the verwendungszweck is not set, then the carFinance can be ignored
        (usage !== LOAN_REQUEST_USAGES.FAHRZEUGKAUF && dirtyState.dirtyFields.verwendungszweck == null) ||
        // if the usage type is FAHRZEUGKAUF and the verwendungszweck is set, then the carFinance can be ignored
        (usage === LOAN_REQUEST_USAGES.FAHRZEUGKAUF && dirtyState.dirtyFields.verwendungszweck != null);

    const hasOnlyIgnorableDirtyFields = Object.keys(dirtyState.dirtyFields).every(
        (field) => canIgnoreCarFinance && field === 'carFinance',
    );

    return !hasOnlyIgnorableDirtyFields && dirtyState.dirty;
};

type Status = {
    error?: unknown;
    saved: boolean;
    position?: number;
};
export type SetSubmitStatus = (status: { error?: unknown; saved: boolean; position?: number }) => void;

type SubmitHandler = (
    setSubmitStatus: SetSubmitStatus,
) => (
    e?: BaseSyntheticEvent<React.FormEvent<HTMLFormElement>, HTMLFormElement, HTMLFormElement> | undefined,
) => Promise<void>;

export type BankCreditRequestFormContextType = {
    registerSubmit: (submitHandler: SubmitHandler) => void;
    registerDirtyState: ({ dirty, dirtyFields }: DirtyState) => void;
    registerReset: (reset: () => void) => void;
    reset: () => void;
    trigger: () => Promise<Status>;
    isDirty: () => boolean;
    hasDirtyForm: boolean;
};

const [useBankCreditRequestFormContext, ContextProvider] =
    createRequiredContext<BankCreditRequestFormContextType>('BankCreditRequestFormContext');

type Props = {
    children: React.ReactNode;
    leadId?: string;
};

const BankCreditRequestFormContextProvider: React.FC<Props> = ({ children, leadId }) => {
    const queryClient = useQueryClient();
    const { data: lead } = useLead(leadId || '');
    const { t } = useTranslation();
    const ref = useRef<SubmitHandler | null>(null);
    const dirtyStateRef = useRef<DirtyState | null>(null);
    const resetStateRef = useRef<() => void>(null);
    const [hasDirtyForm, setHasDirtyForm] = useState(false);

    const registerDirtyState = useCallback(
        (dirtyState: DirtyState) => {
            dirtyStateRef.current = dirtyState;
            setHasDirtyForm(isDirtyState(dirtyState, lead?.finanzbedarf.finanzierungszweck));
        },
        [lead?.finanzbedarf.finanzierungszweck],
    );

    const registerSubmit = useCallback(
        (handleSubmit: SubmitHandler) => {
            ref.current = handleSubmit;
        },
        [ref],
    );

    const registerReset = useCallback(
        (reset: () => void) => {
            resetStateRef.current = reset;
        },
        [resetStateRef],
    );

    const isDirty = useCallback(() => {
        return isDirtyState(dirtyStateRef.current, lead?.finanzbedarf.finanzierungszweck);
    }, [lead?.finanzbedarf.finanzierungszweck]);

    const reset = useCallback(() => {
        resetStateRef.current?.();
        // NOTE: the form may unmount in the same commit as this call (e.g. when discarding changes
        // also switches tabs), so we can't rely on the form's formState effect to report itself
        // clean afterwards - clear it here so isDirty() is correct immediately.
        dirtyStateRef.current = { dirty: false, dirtyFields: {} };
        setHasDirtyForm(false);
    }, []);

    const trigger = useCallback(async () => {
        const handleSubmit = ref.current;

        if (!handleSubmit) return { saved: false };

        let status: Status = { saved: false };

        try {
            await handleSubmit((s) => {
                status = s;
            })();
        } catch (error) {
            status = { error, saved: false };
        }

        const queryKey = queryKeys.getOfferRequestConditionsLatest(leadId ?? '');
        if (status.position) setQueryPosition(JSON.stringify(queryKey), status.position);
        if (status.saved) await queryClient.invalidateQueries({ queryKey });

        if (status.error) toast.error(t('BANK_CREDIT_REQUEST.SAVE_FAILED'));
        else if (status.saved) toast.success(t('BANK_CREDIT_REQUEST.SAVE_SUCCESS'));
        if (!status.error) {
            dirtyStateRef.current = { dirty: false, dirtyFields: {} };
            setHasDirtyForm(false);
        }
        return status;
    }, [leadId, queryClient, t]);

    const value = useMemo(
        () => ({
            hasDirtyForm,
            isDirty,
            registerDirtyState,
            registerReset,
            registerSubmit,
            reset,
            trigger,
        }),
        [hasDirtyForm, isDirty, registerDirtyState, registerSubmit, registerReset, reset, trigger],
    );

    return <ContextProvider value={value}>{children}</ContextProvider>;
};

// eslint-disable-next-line react-refresh/only-export-components
export { useBankCreditRequestFormContext };
export default BankCreditRequestFormContextProvider;
